import { VSCPartJson } from '../../../interfaces/VersaBuilt.Interface';

/**
 * Part-specific values: they vary with every part, so a new program starts
 * with them blank and can't be saved until they're filled in.
 */
export const REQUIRED_NEW_PROGRAM_KEYS = new Set([
	'width',
	'length',
	'partHeight',
	'preOp1Weight',
	'pickHeight',
	'yPushHeight',
	'op1to2TransferHeight',
	'postOp1Weight',
	'placeHeight',
	'finishedPartHeight',
	'postOp2Weight',
]);

/**
 * A 2-op program with the settings nearly every exported program shares (the
 * most common value across the ERP's existing programs). Lengths are mm and
 * weights kg, like an export. Name and description default to the line item
 * and order names, which is what Fusion 360 exports use.
 */
export const newProgramTemplate = (
	name: string,
	description: string
): VSCPartJson => ({
	name,
	description,
	process: null,
	op_count: 2,
	jaws: ['7 lbs. Jaws', '7 lbs. Jaws'],
	parameters: {
		...Object.fromEntries(
			[...REQUIRED_NEW_PROGRAM_KEYS].map((key) => [key, null])
		),

		showAdvancedPick: true,
		showAdvancedOp1: false,
		showAdvancedTransfer: true,
		showAdvancedOp2: true,
		showAdvancedPlace: false,

		partDetection: true,
		partDetectionTolerance: 6.35,
		findPart: true,
		clapPick: true,
		tunePickTilt: 0,
		op1RobotSettle: true,
		op1SettleAngle: 0,
		customInfeedSpacing: false,
		customStartX: 0,
		customStartY: 0,
		customColCount: 0,
		customRowCount: 0,
		customXSpacing: 0,
		customYSpacing: 0,
		infeedXOffset: 0,
		infeedYOffset: -3.81,

		op1Jaws: '7 lbs. Jaws',
		op1Vise: 1,
		op1YPush: true,
		op1YPushOverride: false,
		op1YPushDistance: 3.81,
		op1YPushXOffset: 0,
		op1ViseSettle: true,
		op1Pressure: 120,
		op1Program: '5001',
		tableLoad: '8000',
		tableLoadVise2: '8000',
		washProgram: '8001',

		transferPartHeight: 0,
		op1to2ClearanceHeight: 50.8,
		op1to2FindPartClearanceHeight: 6.35,
		findPartOnTransfer: false,
		useCalibratedTransfer: false,
		unloadOp1Jaws: false,
		washAfterOpTransfer: false,
		washProgramAfterOpTransfer: '8021',
		tableLoadAfterOpXfer: false,
		tableLoadProgramAfterOpXfer: '8000',
		xViseTransferOffset: 0,
		yViseTransferOffset: 0,
		aboveTransfer: [0, 0, 0, 0, 0, 0],
		calibratedTransfer: [0, 0, 0, 0, 0, 0],

		op2Jaws: '7 lbs. Jaws',
		op2Vise: 2,
		op2RobotSettle: false,
		op2SettleAngle: 0,
		op2ViseSettle: true,
		op2Pressure: 120,
		op2Program: '5002',

		outfeedXOffset: 0,
		outfeedYOffset: 0,
		clapPlace: true,
		findPartPlace: false,
		binDrop: false,

		emptyJawsBlowOff: true,
		op2PartBlowOff: true,
		op2EmptyJawsBlowOff: true,
		op2EmptyJawsBlowOffAfterWash: true,
		wash90degOp1: false,
		wash90degOp2: false,
		op1UniversalWash: false,
		op2UniversalWash: false,
		dumpCoolantAtInCNCHome: false,
		dumpCoolantAtCNCHome: false,

		versaWashCycles: 3,
		versaWashDunkSpeed: 0.2,
		versaWashDripTime: 30,
		versaWashRotationalDripSpeed: 0.2,
	},
});
